import { useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { useToast } from '@/shared/hooks/use-toast';
import CTAButton from './CTAButton';

const DemoRequestDialog = () => {
  const { toast } = useToast();
  const [showDemoForm, setShowDemoForm] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ email: '', company_name: '' });

  const handleDemoRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/support/demo-request/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowDemoForm(false);
        setShowSuccessMessage(true);
        setFormData({ email: '', company_name: '' });
      } else {
        throw new Error(data.message || 'Failed to submit demo request');
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to submit. Please try again or contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={showDemoForm} onOpenChange={setShowDemoForm}>
        <DialogTrigger asChild>
          <CTAButton className="px-6 sm:px-8 py-2 sm:py-3 text-base sm:text-lg" fullWidthMobile={false}>
            Request a Demo
          </CTAButton>
        </DialogTrigger>
        <DialogContent className="bg-maintenance-navy text-white border-white max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-lg sm:text-xl">Request a Demo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDemoRequest} className="space-y-4">
            <div>
              <Label htmlFor="demo_email" className="text-white text-sm sm:text-base">
                Email *
              </Label>
              <Input
                id="demo_email"
                type="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={isSubmitting}
                className="bg-white text-black"
              />
            </div>
            <div>
              <Label htmlFor="company_name" className="text-white text-sm sm:text-base">
                Company Name *
              </Label>
              <Input
                id="company_name"
                type="text"
                placeholder="Enter company name"
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                required
                disabled={isSubmitting}
                className="bg-white text-black"
              />
            </div>
            <div className="flex justify-center">
              <Button
                type="submit"
                disabled={isSubmitting}
                className={`bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors text-sm sm:text-base`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {showSuccessMessage && (
        <Alert className="bg-maintenance-navy/90 border-white text-white mt-4">
          <AlertDescription>
            Your demo request has been submitted. If approved, our team will email you with a link
            to your demo shortly!
            <Button
              onClick={() => setShowSuccessMessage(false)}
              className={`ml-4 bg-maintenance-navy text-white hover:bg-maintenance-blue border border-white/30 hover:border-white transition-colors`}
            >
              Close
            </Button>
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};

export default DemoRequestDialog;
